import { useState } from 'react';
import { Dialog, DialogContent, TextField, Button, Typography, useTheme, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from '@mui/material';
import { Info, LogOut, ChartLine } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../types/store';
import AppActions from '../../actions/app-actions';
import AppUtils from '../../utils/app-utils';
import UIError from '../ui-error';
import UIIcon from '../ui-icon';

export default function UserPopup({ open, onClose }: { open: boolean; onClose: () => void; }) {
    const theme = useTheme(); // Access the theme
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const update = useSelector((state: RootState) => state.auth.update);
    const translate = useSelector((state: RootState) => state.ui.language.auth);

    // Update form state
    const [nickname, setNickname] = useState(user.nickname);

    function setPreferedNickname(nickname: string) {
        setNickname(nickname);
    }

    function onUpdate() {
        dispatch(AppActions.Update({ token: AppUtils.getLoginCookie(), nickname: nickname }));
    }

    function onLogout() {
        const loginToken = AppUtils.getLoginCookie();
        if (loginToken !== undefined) {
            dispatch(AppActions.Logout({ token: loginToken }));
        }
    }

    return (
        <Dialog closeAfterTransition={false} open={open} onClose={onClose} disableRestoreFocus={false} maxWidth="sm" fullWidth>
            <DialogContent>
                <Typography variant="h6" gutterBottom>Your profile</Typography>

                <TextField margin="normal" fullWidth name="nickname" label={translate.preferredNickname} type="text" defaultValue={user.nickname}
                    onChange={(e) => setPreferedNickname(e.target.value)}
                />

                <UIError message={update.error} />
                <Button startIcon={<Info />} onClick={onUpdate} disabled={update.isLoading} fullWidth variant="contained" sx={{ mt: 0, mb: 3, bgcolor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.dark, } }}>
                    {translate.buttonUpdate}
                </Button>

                <TableContainer component={Paper} sx={{ maxWidth: 600, margin: 'auto', mt: 2, mb: 2 }}>
                    <Typography variant="h6" align="center" gutterBottom sx={{ pt: 2 }}>
                        <UIIcon icon={ChartLine} /> {translate.stats}
                    </Typography>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>{translate.gamesHosted}</TableCell><TableCell align="right">{user.stats.games_hosted}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate.gamesPlayed}</TableCell><TableCell align="right">{user.stats.games_played}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate.gamesWon}</TableCell><TableCell align="right">{user.stats.games_won}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate.gamesLost}</TableCell><TableCell align="right">{user.stats.games_lost}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate.totalScore}</TableCell><TableCell align="right">{user.stats.total_score}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>{translate.averageScore}</TableCell><TableCell align="right">{user.stats.games_played > 0 ? (user.stats.total_score / user.stats.games_played).toFixed(1) : 0}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Button startIcon={<LogOut />} onClick={onLogout} fullWidth color="error" variant="outlined" >
                    {translate.buttonLogout}
                </Button>
            </DialogContent>
        </Dialog>
    );
}